/* ============================================================
   SHAILPIYA — Global Interactions
   ============================================================
   • Custom cursor (dot + magnetic ring)
   • Scroll reveal observer
   • Page transition curtain
   • Magnetic hover for buttons
   • On-load hash scroll (cross-page anchor links)
   • Reduced-motion aware
   • Mobile-aware (cursor off on touch)
   ============================================================ */

(function () {
  'use strict';

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const IS_TOUCH = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
  const IS_NARROW = window.innerWidth < 860;



  // ============================================================
  // CUSTOM CURSOR — DISABLED (was hiding native cursor on desktop)
  // Native mouse cursor is now used everywhere. Any lingering
  // sp-cursor-dot / sp-cursor-ring elements from prior sessions
  // are removed defensively.
  // ============================================================
  document.documentElement.style.cursor = '';
  document.body.style.cursor = '';
  document.querySelectorAll('.sp-cursor-dot, .sp-cursor-ring').forEach(el => el.remove());

  // ============================================================
  // SCROLL REVEAL
  // ============================================================
  if (!REDUCED_MOTION && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(entries => {
      entries.forEach(en => {
        if (en.isIntersecting) {
          en.target.classList.add('sp-in');
          io.unobserve(en.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' });

    function bindReveals() {
      document.querySelectorAll('.sp-reveal:not(.sp-in)').forEach(el => io.observe(el));

      // Auto-reveal common section blocks if author hasn't added .sp-reveal explicitly
      document.querySelectorAll('section h1, section h2, section h3, section p, section img').forEach(el => {
        if (el.dataset.spAutorevealed) return;
        // skip if inside hero or above the fold
        if (el.closest('.sp-hero-, [class*="sp-hero-"], .sp-no-reveal')) return;
        el.classList.add('sp-auto-reveal');
        el.dataset.spAutorevealed = '1';
        io.observe(el);
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bindReveals);
    } else {
      bindReveals();
    }

    // Re-bind when Shopify section editor updates
    document.addEventListener('shopify:section:load', bindReveals);
  }

  // ============================================================
  // MAGNETIC HOVER (for primary buttons)
  // ============================================================
  if (!IS_TOUCH && !REDUCED_MOTION) {
    function bindMagnetic() {
      document.querySelectorAll('[data-magnetic]:not([data-magnetic-bound])').forEach(el => {
        el.dataset.magneticBound = '1';
        const strength = parseFloat(el.dataset.magnetic) || 0.25;
        el.addEventListener('mousemove', e => {
          const r = el.getBoundingClientRect();
          const x = (e.clientX - (r.left + r.width / 2)) * strength;
          const y = (e.clientY - (r.top + r.height / 2)) * strength;
          el.style.transform = `translate(${x}px, ${y}px)`;
        });
        el.addEventListener('mouseleave', () => {
          el.style.transform = '';
        });
      });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bindMagnetic);
    } else {
      bindMagnetic();
    }
    document.addEventListener('shopify:section:load', bindMagnetic);
  }

  // ============================================================
  // ON-LOAD HASH SCROLL (reliable cross-page anchor links)
  // When arriving on a page with a #hash (e.g. /#sp-story from
  // a product page), the browser often tries to jump before the
  // content has laid out, landing at the top. This re-scrolls to
  // the target once things settle, accounting for the sticky header.
  // ============================================================
  (function () {
    if (!window.location.hash) return;
    var rawHash = window.location.hash;
    // Guard against invalid selectors
    function targetEl() {
      try { return document.querySelector(rawHash); } catch (_) { return null; }
    }
    function doScroll() {
      var el = targetEl();
      if (!el) return;
      var top = el.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top: top, behavior: 'smooth' });
    }
    function settleScroll() {
      // give lazy sections/images a beat to lay out, then land on target
      setTimeout(doScroll, 300);
      setTimeout(doScroll, 650);
    }
    if (document.readyState === 'complete') {
      settleScroll();
    } else {
      window.addEventListener('load', settleScroll);
    }
  })();

  // ============================================================
  // PAGE TRANSITION (curtain wipe on internal nav clicks)
  // Two-phase: this handler plays the COVER-UP on leave.
  // The early inline script in sp-brand-tokens plays the
  // RECEDE-DOWN reveal when the next page loads.
  // ============================================================
  if (!REDUCED_MOTION) {
    let curtain = window.__spCurtain;
    if (!curtain) {
      curtain = document.createElement('div');
      curtain.className = 'sp-curtain';
      curtain.innerHTML = '<span></span><span></span><span></span><span></span><span></span>';
      document.body.appendChild(curtain);
      window.__spCurtain = curtain;
    }

    // Cover up on leave, then navigate (new page reveals itself by receding down)
    document.addEventListener('click', e => {
      const a = e.target.closest('a');
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) return;
      if (a.target === '_blank' || a.hasAttribute('download')) return;
      // Only internal navigation
      try {
        const url = new URL(href, window.location.href);
        if (url.origin !== window.location.origin) return;
        if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      } catch (_) { return; }
      // Don't transition on Shopify admin design-mode
      if (document.documentElement.classList.contains('shopify-design-mode')) return;
      e.preventDefault();
      curtain.classList.remove('sp-curtain-reveal');
      curtain.classList.remove('sp-cover-now');
      curtain.classList.add('sp-curtain-in');
      setTimeout(() => { window.location.href = href; }, 420);
    });
  }

  // ============================================================
  // ANNOUNCE READY
  // ============================================================
  document.documentElement.classList.add('sp-ready');
})();